import axios from "axios";
import envConfig from "../../envConfig";
import AppAuth from "@toothfairy/shared-ui/AppAuthScreen";
import secureRequest from "@toothfairy/shared-api/secureAPI";
import apiClient from "@toothfairy/shared-api/apiClient";

const CONTENT_TYPE_MAP = {
  pdf: "application/pdf",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ppt: "application/vnd.ms-powerpoint",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  svg: "image/svg+xml",
  webp: "image/webp",
  bmp: "image/bmp",
};

const SUPPORTED_EXTENSIONS = new Set(Object.keys(CONTENT_TYPE_MAP));

const MAX_FILE_SIZE = 50 * 1024 * 1024;

const getContentType = (ext) => CONTENT_TYPE_MAP[ext.toLowerCase()] || "application/octet-stream";

const isSupportedExtension = (ext) => SUPPORTED_EXTENSIONS.has(ext.toLowerCase());

const getPresignedUploadUrl = async ({ filename, contentType, workspaceid }) => {
  const payload = await AppAuth.getCurrentSession();
  const importType = contentType.startsWith("image/")
    ? "imported-image"
    : "imported_doc_files";
  const url = `${envConfig.REACT_APP_REST_ENDPOINT}/documents/requestUploadURL?filename=${workspaceid}/${filename}&importType=${importType}${contentType ? `&contentType=${contentType}` : ""}`;
  const result = await secureRequest(apiClient.apiClient, "GET", url, null, null, true, null, payload);
  return JSON.parse(result?.body)?.uploadURL;
};

const getPresignedDownloadUrl = async ({ s3Key, workspaceid }) => {
  const payload = await AppAuth.getCurrentSession();
  const filename = s3Key.split("/").pop();
  const url = `${envConfig.REACT_APP_REST_ENDPOINT}/documents/requestDownloadURL?filename=${s3Key}&type=imported_doc_files&workspaceid=${workspaceid}&logId=pull_push&context=agent`;
  const result = await secureRequest(apiClient.apiClient, "GET", url, null, null, true, null, payload);
  return JSON.parse(result?.body)?.downloadURL;
};

const uploadFileToS3 = async ({ presignedUrl, data, contentType }) => {
  const response = await axios.request({
    method: "put",
    url: presignedUrl,
    data,
    headers: { "Content-Type": contentType },
    timeout: 120000,
  });
  return response.status === 200;
};

const downloadFileFromS3 = async (presignedUrl) => {
  const response = await axios.get(presignedUrl, {
    responseType: "arraybuffer",
    timeout: 120000,
  });
  return response.data;
};

export const handlePullFile = async ({
  filePath,
  boundFolderPath,
  workspaceid,
}) => {
  const resolvePath = (relativePath) => {
    if (!relativePath || !boundFolderPath) return null;
    if (relativePath.startsWith(boundFolderPath)) return relativePath;
    const separator = boundFolderPath.endsWith("/") || boundFolderPath.endsWith("\\")
      ? ""
      : "/";
    return boundFolderPath + separator + relativePath;
  };

  const validatePath = (targetPath) => {
    if (!targetPath || !boundFolderPath) return false;
    const normalizedBound = boundFolderPath.replace(/\\/g, "/").toLowerCase();
    const normalizedTarget = targetPath.replace(/\\/g, "/").toLowerCase();
    return normalizedTarget.startsWith(normalizedBound);
  };

  const ext = filePath.split(".").pop();
  if (!isSupportedExtension(ext)) {
    return {
      success: false,
      error: `File type '.${ext}' not supported. Supported: ${[...SUPPORTED_EXTENSIONS].join(", ")}`,
    };
  }

  const absolutePath = resolvePath(filePath);
  if (!absolutePath || !validatePath(absolutePath)) {
    return { success: false, error: `Path ${filePath} is outside the bound folder` };
  }

  try {
    if (!window?.electronAPI?.readFileBinary) {
      return { success: false, error: "Binary file read not available (requires Electron)" };
    }

    const fileData = await window.electronAPI.readFileBinary(absolutePath);
    if (!fileData?.success) {
      return { success: false, error: fileData?.error || "Failed to read file" };
    }

    if (fileData.size && fileData.size > MAX_FILE_SIZE) {
      return {
        success: false,
        error: `File exceeds 50 MB limit (${(fileData.size / 1024 / 1024).toFixed(2)} MB)`,
      };
    }

    const filename = filePath.split("/").pop().split("\\").pop();
    const timestampFilename = `${Date.now()}_${filename}`;
    const contentType = getContentType(ext);
    const s3Key = `imported_doc_files/${workspaceid}/${timestampFilename}`;

    const presignedUrl = await getPresignedUploadUrl({
      filename: timestampFilename,
      contentType,
      workspaceid,
    });

    if (!presignedUrl) {
      return { success: false, error: "Failed to generate upload URL" };
    }

    const binaryData = _base64ToArrayBuffer(fileData.data);
    const uploadSuccess = await uploadFileToS3({
      presignedUrl,
      data: binaryData,
      contentType,
    });

    if (!uploadSuccess) {
      return { success: false, error: "Failed to upload file to S3" };
    }

    return {
      success: true,
      s3_key: s3Key,
      filename: timestampFilename,
      content_type: contentType,
      original_path: filePath,
      size: fileData.size,
    };
  } catch (error) {
    console.error("[PULL_FILE] Error:", error);
    return { success: false, error: error.message || "Pull file failed" };
  }
};

export const handlePushFile = async ({
  s3Key,
  localPath,
  filename,
  boundFolderPath,
  workspaceid,
}) => {
  const resolvePath = (relativePath) => {
    if (!relativePath || !boundFolderPath) return null;
    if (relativePath.startsWith(boundFolderPath)) return relativePath;
    const separator = boundFolderPath.endsWith("/") || boundFolderPath.endsWith("\\")
      ? ""
      : "/";
    return boundFolderPath + separator + relativePath;
  };

  const validatePath = (targetPath) => {
    if (!targetPath || !boundFolderPath) return false;
    const normalizedBound = boundFolderPath.replace(/\\/g, "/").toLowerCase();
    const normalizedTarget = targetPath.replace(/\\/g, "/").toLowerCase();
    return normalizedTarget.startsWith(normalizedBound);
  };

  const absoluteDir = resolvePath(localPath);
  if (!absoluteDir || !validatePath(absoluteDir)) {
    return { success: false, error: `Path ${localPath} is outside the bound folder` };
  }

  try {
    if (!window?.electronAPI?.writeFileBinary) {
      return { success: false, error: "Binary file write not available (requires Electron)" };
    }

    const presignedUrl = await getPresignedDownloadUrl({
      s3Key,
      workspaceid,
    });

    if (!presignedUrl) {
      return { success: false, error: "Failed to generate download URL" };
    }

    const binaryData = await downloadFileFromS3(presignedUrl);

    const finalFilename = filename || s3Key.split("/").pop();
    const fullPath = _joinPath(absoluteDir, finalFilename);

    const writeResult = await window.electronAPI.writeFileBinary(fullPath, binaryData, {
      createDir: true,
    });

    if (!writeResult?.success) {
      return { success: false, error: writeResult?.error || "Failed to write file locally" };
    }

    const relativeSavePath = _joinPath(localPath, finalFilename);

    return {
      success: true,
      local_path: relativeSavePath,
      original_s3_key: s3Key,
    };
  } catch (error) {
    console.error("[PUSH_FILE] Error:", error);
    return { success: false, error: error.message || "Push file failed" };
  }
};

const _base64ToArrayBuffer = (base64) => {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
};

const _joinPath = (...parts) => {
  return parts.join(parts[0]?.includes("\\") ? "\\" : "/").replace(/[/\\]+/g, (m, offset) => {
    return offset === 0 ? m : m[0];
  });
};

export default {
  handlePullFile,
  handlePushFile,
  isSupportedExtension,
  CONTENT_TYPE_MAP,
};
